export interface CategoryStandingInput {
  id: string;
  raceId: string;
  category?: string | null;
  categoryRank?: number | null;
  position?: number | null;
  timeSeconds?: number | null;
  gender?: string | null;
}

export interface CategoryStanding {
  rank?: number;
  participantCount: number;
}

export function normalizeStandingCategory(category: string): string {
  const normalized = category
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s?\([^)]*\)/g, '').trim();
  // PDF headers can be appended to an otherwise valid official category code.
  const officialCode = normalized.match(/^(H?[CVT](?:M|P|T)?[FH](?:V[123]|[SJCMBA]))(?:\s|$)/)?.[1];
  return officialCode ?? normalized.replace(/[^A-Z0-9]/g, '');
}

function normalizeGender(gender: string | null | undefined, category: string): string {
  const codeGender = normalizeStandingCategory(category).match(/^H?[CVT](?:M|P|T)?(F|H)/)?.[1];
  if (codeGender) return codeGender;
  const normalizedGender = (gender ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();

  if (['F', 'FEMALE', 'FEMME', 'WOMAN', 'WOMEN'].includes(normalizedGender)) return 'F';
  if (['H', 'M', 'MALE', 'HOMME', 'MAN', 'MEN'].includes(normalizedGender)) return 'H';

  // Official FFSLC codes carry the gender after the discipline prefix:
  // CFS/CHS, VFJ/VHJ, TFV1/THV1, CMFS/CMHS, CPFS/CPHS, etc.
  return '';
}

function positiveNumber(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function rankingMetric(row: CategoryStandingInput): { type: 'position' | 'time'; value: number } | null {
  const time = positiveNumber(row.timeSeconds);
  // A stored position must not make a no-time/DNF participant rank as a
  // finisher. Those rows are deliberately left unranked and sorted last.
  if (time === undefined) return null;

  const position = positiveNumber(row.position);
  if (position !== undefined) return { type: 'position', value: position };

  return { type: 'time', value: time };
}

/**
 * Calculates missing category standings inside one race, one category and one
 * gender. An imported category_rank remains authoritative when it is present.
 */
export function calculateCategoryStandings(
  rows: CategoryStandingInput[],
): Record<string, CategoryStanding> {
  const groups = new Map<string, CategoryStandingInput[]>();

  for (const row of rows) {
    if (!row.category?.trim()) continue;
    const category = normalizeStandingCategory(row.category);
    if (!category) continue;
    const gender = normalizeGender(row.gender, row.category);
    const key = `${row.raceId}|${category}|${gender}`;
    const group = groups.get(key) ?? [];
    group.push(row);
    groups.set(key, group);
  }

  const standings: Record<string, CategoryStanding> = {};

  for (const group of groups.values()) {
    const sorted = [...group].sort((a, b) => {
      const metricA = rankingMetric(a);
      const metricB = rankingMetric(b);
      if (!metricA && !metricB) return a.id.localeCompare(b.id);
      if (!metricA) return 1;
      if (!metricB) return -1;
      if (metricA.type !== metricB.type) return metricA.type === 'position' ? -1 : 1;
      return metricA.value - metricB.value || a.id.localeCompare(b.id);
    });

    let previousMetric = '';
    let derivedRank = 0;

    sorted.forEach((row, index) => {
      const metric = rankingMetric(row);
      const metricKey = metric ? `${metric.type}:${metric.value}` : '';
      if (metricKey && metricKey !== previousMetric) derivedRank = index + 1;
      if (metricKey) previousMetric = metricKey;

      standings[row.id] = {
        rank: metricKey ? positiveNumber(row.categoryRank) ?? derivedRank : undefined,
        participantCount: group.length,
      };
    });
  }

  return standings;
}
